import { Grid, Icons, Stack } from '@/theme/index';
import { SectionTitle, TileItem, GridItem } from '../components';
import { getFraudTileText } from '../utils';

export interface FraudIpAdditionalDetailsProps {
  isDynamicConnection?: boolean;
  isSharedConnection?: boolean;
  isTrustedNetwork?: boolean;
  isSecurityScanner?: boolean;
  isMobile?: boolean;
  isCrawler?: boolean;
  host: string;
  asn: string;
}

export function FraudIpAdditionalDetails({
  isDynamicConnection,
  isSharedConnection,
  isTrustedNetwork,
  isSecurityScanner,
  isMobile,
  host,
  asn,
  isCrawler,
}: FraudIpAdditionalDetailsProps) {
  return (
    <Stack>
      <SectionTitle>Additional Details</SectionTitle>

      <Grid container spacing={2}>
        <GridItem>
          <TileItem
            title="Dynamic Connection:"
            text={getFraudTileText(isDynamicConnection)}
            IconComponent={Icons.Info}
            tooltipText="Indicates that the IP address is using dynamic assignment protocols, suggesting this user’s IP address will likely change in the near future."
            testLabelId="ip-additional-details-dynamic-connection-label"
            testContentId="ip-additional-details-dynamic-connection"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Shared Connection:"
            text={getFraudTileText(isSharedConnection)}
            IconComponent={Icons.Info}
            tooltipText="IP addresses using a shared connection are likely to have more than a few users active on the IP address at the same time. This could include mobile networks, corporate exit points, libraries, coffee shops, hotel lobbies, dorms, hospitals, company VPNs, etc."
            testLabelId="ip-additional-details-shared-connection-label"
            testContentId="ip-additional-details-shared-connection"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Trusted Network:"
            text={getFraudTileText(isTrustedNetwork)}
            IconComponent={Icons.Info}
            tooltipText="Identifies company networks and corporate access points that have low abuse rates and high security protocols. Note, however, that IP addresses on trusted networks could still be compromised by malware."
            testLabelId="ip-additional-details-trusted-network-label"
            testContentId="ip-additional-details-trusted-network"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Security Scanner Detected:"
            text={getFraudTileText(isSecurityScanner)}
            IconComponent={Icons.Info}
            tooltipText="Indicates whether the IP address is likely associated with a verified online security scanner or endpoint by a trusted security vendor such as Tenable, Qualys, or similar provider."
            testLabelId="ip-additional-details-security-scanner-detected-label"
            testContentId="ip-additional-details-security-scanner-detected"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Mobile Device:"
            text={getFraudTileText(isMobile)}
            IconComponent={Icons.Info}
            tooltipText="Indicates if this IP address has been associated with a mobile browser user agent. This value will show as No if the user agent is unknown."
            testLabelId="ip-additional-details-mobile-device-label"
            testContentId="ip-additional-details-mobile-device"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Hostname:"
            text={host}
            IconComponent={Icons.Info}
            tooltipText="Provides the host of the IP address, if one is available."
            testLabelId="ip-additional-details-host-label"
            testContentId="ip-additional-details-host"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="ASN:"
            text={asn}
            IconComponent={Icons.Info}
            tooltipText="Provides the Autonomous System Number, if one is available."
            testLabelId="ip-additional-details-asn-label"
            testContentId="ip-additional-details-asn"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Search Engine Crawler:"
            text={getFraudTileText(isCrawler)}
            IconComponent={Icons.Info}
            tooltipText="Indicates whether the IP address is likely to be a crawler associated with a mainstream search engine such as Google, Bing, Yandex, etc. These crawlers are common and used to support search engines’ operations."
            testLabelId="ip-additional-details-search-engine-crawler-label"
            testContentId="ip-additional-details-search-engine-crawler"
          />
        </GridItem>
      </Grid>
    </Stack>
  );
}
